/**
 * Shared shell for the four auth pages (sign in, register, forgot, reset).
 *
 * Laid out as an answer-book cover sheet: an ink panel carrying the mark and
 * the subject register, and a white side carrying the fields — the artifact
 * these learners actually handle in an exam room. The ochre rule across the
 * top is the seal line. Everything else is kept quiet so the cover is the one
 * thing the page is remembered by.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Logo } from "./Logo";

const SUBJECTS = [
  "Mathematics",
  "Mathematical Literacy",
  "Accounting",
  "English",
];

type AuthLayoutProps = {
  /** Page heading, e.g. "Sign in". */
  title: string;
  /** Optional sentence under the heading explaining what this page does. */
  intro?: ReactNode;
  /** The form, or whatever the page's main body is. */
  children: ReactNode;
  /** Links below the body — "Forgot your password?", "Create account", etc. */
  footer?: ReactNode;
};

export function AuthLayout({ title, intro, children, footer }: AuthLayoutProps) {
  return (
    <div className="auth-shell">
      <div className="auth-sheet">
        <aside className="auth-cover">
          <Link to="/" className="auth-cover-brand">
            <Logo size={34} />
            <span className="auth-cover-wordmark">PyDialog</span>
          </Link>

          <p className="auth-cover-line">
            Answer in your own words. Get marked on your reasoning, not just
            your final answer.
          </p>

          <div className="auth-cover-register">
            <span className="auth-cover-eyebrow">Revision · Grades 10–12</span>
            <ul>
              {SUBJECTS.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          </div>
        </aside>

        <main className="auth-panel">
          <h1>{title}</h1>
          {intro && <p className="auth-intro">{intro}</p>}
          {children}
          {footer && <div className="auth-footer">{footer}</div>}
        </main>
      </div>
    </div>
  );
}
